import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EditableAssignmentContent,
  StepDetailBody,
  StepDetailHeader,
  StepDetailPanel,
  StepModeSwitch,
  useDebouncedFieldUpdate,
  type EditableAssignmentPatch,
  type StepMode
} from '@bliks-io/ui';
import type { ProcessDraftAssignment, ProcessStepRecordingDisplay } from '@bliks-io/contracts';
import { useUpdateDraftAssignment } from '@/api/mutations/useProcessDraft';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { EditorSuccessorPicker } from '../../components/EditorSuccessorPicker';
import { useDialogOptionsForDraftEditor } from '../adapters/dialogOptions';
import { toUpdateDraftAssignmentRequest } from '../adapters/patchAdapters';
import { useUndoableEditorActions } from '../actions/useUndoableEditorActions';
import { useProcessDraftCache } from '../actions/useProcessDraftCache';
import { DraftIssueSummary } from './DraftIssueSummary';
import { useNodeFieldIssues } from './useNodeFieldIssues';

interface Props {
  assignment: ProcessDraftAssignment;
  processId: string;
  /** The draft's start event — the pickers' suggestion anchor. */
  startEventStableStepId?: string;
  siblingSteps: ProcessStepRecordingDisplay[];
  onClose: () => void;
}

/**
 * Side-panel for a `ProcessDraftAssignment`. Carries only assignee / inputs /
 * helpers — no task content, and no action-type / decision-rule controls.
 */
export function DraftAssignmentPanel({
  assignment,
  processId,
  startEventStableStepId,
  siblingSteps,
  onClose
}: Props) {
  const { t } = useTranslation();
  const updateMutation = useUpdateDraftAssignment(processId);
  const { convertAssignmentToStep, markAssignmentUnclear } = useUndoableEditorActions({
    processId
  });
  const assigneeLookup = useAssigneeLookup();
  const { intermediateEventTypes } = useDialogOptionsForDraftEditor();
  const { getCachedDraftAssignments } = useProcessDraftCache(processId);

  const stableStepId = String(assignment.stableStepId);
  const fieldIssues = useNodeFieldIssues(stableStepId);

  const updateMutationAsync = updateMutation.mutateAsync;
  const handlePatch = useCallback(
    async (patch: EditableAssignmentPatch) => {
      // The debounced name field flushes when the keyed panel unmounts, which
      // can land after this assignment stopped existing — a temp-id
      // placeholder swapped for the server row, or an undo/refetch removing a
      // focused node (no blur fires). PATCHing the dead id 404s, and the
      // mutation's onError rollback would then discard unrelated edits.
      if (!getCachedDraftAssignments().some((a) => String(a.stableStepId) === stableStepId)) return;
      await updateMutationAsync({
        stableStepId,
        patch: toUpdateDraftAssignmentRequest(patch)
      });
    },
    [updateMutationAsync, stableStepId, getCachedDraftAssignments]
  );

  const nameField = useDebouncedFieldUpdate({
    value: assignment.generatedName ?? '',
    onCommit: (next) => void handlePatch({ generatedName: next.trim() ? next : null })
  });

  const stepIndex = siblingSteps.findIndex((s) => s._id === stableStepId);
  const displayStep = siblingSteps[stepIndex];

  const handleModeChange = useCallback(
    (next: StepMode) => {
      if (next === 'manual') void convertAssignmentToStep(stableStepId);
    },
    [convertAssignmentToStep, stableStepId]
  );

  const handleMarkUnclear = useCallback(async () => {
    await markAssignmentUnclear(stableStepId);
    onClose();
  }, [markAssignmentUnclear, stableStepId, onClose]);

  return (
    <StepDetailPanel>
      <StepDetailHeader
        step={displayStep}
        onClose={onClose}
        hideDescription
        editableName={{
          value: nameField.value,
          onChange: nameField.onChange,
          onBlur: nameField.flush,
          placeholder: t('processFlow.stepPrefix', { number: stepIndex + 1 })
        }}
      />
      <StepDetailBody>
        <DraftIssueSummary nodeId={stableStepId} />
        <StepModeSwitch
          mode="viral"
          onChange={handleModeChange}
          labels={{
            manual: t('processDraft.mode.manual'),
            viral: t('processDraft.mode.viral')
          }}
        />
        <EditableAssignmentContent
          step={displayStep}
          onPatch={handlePatch}
          fieldIssues={fieldIssues}
          assigneeLookup={assigneeLookup}
          eventTypes={intermediateEventTypes}
          processSteps={siblingSteps}
          renderSuccessorPicker={(pickerProps) => (
            <EditorSuccessorPicker
              {...pickerProps}
              processId={processId}
              recordingId={startEventStableStepId}
              pendingSuggestions={displayStep?.entitySuggestions}
            />
          )}
          allowUnclear
          onMarkUnclear={handleMarkUnclear}
        />
      </StepDetailBody>
    </StepDetailPanel>
  );
}
